/**
 * Watch — native playback engine per platform.
 *
 *   • Electron (Windows): bundled mpv (Windows Media Foundation + DirectShow +
 *     DXVA). Renderer hands the URL to the main process; mpv plays in its
 *     own native window.
 *   • Android (phone / tablet / TV): WebView <video> is backed by Android's
 *     own MediaCodec stack (ExoPlayer-grade hardware decoding) and handles
 *     HLS natively. AC3/EAC3-only streams can be handed off to VLC for
 *     Android via an `ACTION_VIEW` intent.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Hls from "hls.js";
import mpegts from "mpegts.js";
import { PLAYER } from "../lib/testIds";
import { addToHistory, saveHistoryPosition, getResumePosition } from "../lib/watchHistory";
import { markWatched } from "../lib/watchedItems";
import { isElectron, isAndroid } from "../lib/deviceId";
import { isNativePlayerAvailable, playWithNativeExoPlayer, getSelectedEngine } from "../lib/nativePlayer";
import { checkConnectionLimit } from "../lib/connectionLimit";
import { ArrowLeft, AlertTriangle, Cpu, Loader2 } from "lucide-react";
import PlayerOverlay from "../components/PlayerOverlay";
import LiveTvOverlay from "../components/LiveTvOverlay";
import PlayerTrackPicker from "../components/PlayerTrackPicker";
import UpNextCountdown, { COUNTDOWN_SECONDS } from "../components/UpNextCountdown";
import GetNativeAppBanner from "@/components/GetNativeAppBanner";
// v1.0.9 — Movie Cue integration.
import {
  getCue,
  removeFromCue,
  getIntermissionMinutes,
} from "../lib/movieCue";
import IntermissionOverlay from "../components/IntermissionOverlay";
import { applyPreferredAudioLang } from "../lib/audioLangPreference";

export default function Watch() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const initialUrl = location.state?.stream_url || params.get("stream_url");
  const fallbackUrl = location.state?.fallback_url || null;
  const name = location.state?.name || params.get("name") || "Now playing";
  const logoUrl = location.state?.logo || params.get("logo") || null;
  const contentType = location.state?.type || params.get("type") || null;
  // v1.4.30 — series metadata forwarded from Browse.playItem so the
  // watch-history layer can aggregate episodes under one show card in
  // Continue Watching.
  const seriesId = location.state?.series_id || null;
  const seriesName = location.state?.series_name || null;
  const seasonNum = location.state?.season ?? null;
  const episodeNum = location.state?.episode ?? null;
  const episodeQueue = Array.isArray(location.state?.episode_queue)
    ? location.state.episode_queue
    : null;
  // v1.0.9 — Movie Cue mode. Set by Browse when the user hits
  // "Play all in cue". On this run, when playback finishes we:
  //   1. Remove the current movie from the cue,
  //   2. Show the cinematic intermission overlay for N minutes,
  //   3. Auto-load the next queued movie (if any).
  const cueMode = !!location.state?.cue_mode;
  // v1.3.94 — TMDB metadata for the custom Movies/Series overlay (poster,
  // rating, genre, release date). Passed from Browse.playItem when the
  // user clicks Play on a VOD tile. Live TV never has these.
  const posterUrl = location.state?.poster || location.state?.cover || logoUrl;
  const rating = location.state?.rating ?? null;
  const genre = location.state?.genre || null;
  const releaseDate = location.state?.release_date || location.state?.date || null;
  // v1.3.95 — Live TV overlay props (EPG programme window + zap queue).
  const channelGroup = location.state?.group || "";
  const channelId = location.state?.channel_id || "";
  const currentProg = location.state?.current_programme || null;
  const nextProg = location.state?.next_programme || null;
  const zapQueue = Array.isArray(location.state?.zap_queue)
    ? location.state.zap_queue
    : null;
  // Only VOD (movies / series episodes) shows the custom overlay; live TV
  // has its own EPG overlay in LiveTvWatch.jsx.
  const isVod = contentType === "movie" || contentType === "series" || contentType === "episode";
  const isLiveTv = contentType === "live_tv";

  // Zap navigation — find current channel in the queue and route to the
  // neighbouring channel's `/player/watch` with its own EPG state. When
  // the queue is missing (legacy callers), zapping is disabled.
  const zapIdx = zapQueue && channelId ? zapQueue.findIndex((c) => c.id === channelId) : -1;
  const canZapUp = zapQueue && zapIdx > 0;
  const canZapDown = zapQueue && zapIdx >= 0 && zapIdx < zapQueue.length - 1;
  const zapTo = (nextCh) => {
    if (!nextCh) return;
    navigate("/player/watch", {
      state: {
        stream_url: nextCh.stream_url,
        name: nextCh.name,
        logo: nextCh.logo_url || "",
        type: "live_tv",
        group: nextCh.group || "",
        channel_id: nextCh.id || "",
        // EPG for the new channel gets refetched via LiveTvWatch on
        // re-entry — for the direct hop we pass null and the panel
        // falls back to just the channel name.
        current_programme: null,
        next_programme: null,
        zap_queue: zapQueue,
      },
      replace: true,
    });
  };

  // v1.3.96 — Track picker modal state. Both overlays use the same
  // picker so subtitle / audio-track / playback-settings look
  // identical in Movies and Live TV.
  const [picker, setPicker] = useState(null); // "subtitles" | "audio" | "settings" | null

  // v1.0.9 — Movie Cue state. `cueIntermission` is the next queued
  // movie whose intermission we're currently showing. Null means
  // playback is active (or we're not in cue mode). Set immediately
  // after the current cue movie finishes.
  const [cueIntermission, setCueIntermission] = useState(null);
  // v1.0.9 — Native player: watch for the WebView returning to the
  // foreground. When the ExoPlayer activity finishes (either because
  // the movie ended or the user pressed Back), Android brings the
  // Capacitor WebView back to the front and fires a
  // visibilitychange -> visible event on the document. We treat that
  // as "native playback ended" and advance the cue.
  useEffect(() => {
    if (!cueMode || contentType !== "movie") return;
    if (!isAndroid()) return;
    // Ignore the FIRST visibility flip that happens on mount because
    // Watch.jsx renders BEFORE the native activity has fully covered
    // the WebView on some devices. We wait for the first "hidden"
    // event before we start listening for the "visible" that follows.
    let sawHidden = false;
    const onVis = () => {
      if (document.hidden) {
        sawHidden = true;
        return;
      }
      if (sawHidden) {
        // Native activity finished; foreground us. Advance the cue.
        advanceCue();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cueMode, contentType]);
  // Handler shared by the native "activity resumed" listener and the
  // web <video> ended handler. Consumes the head of the cue, then
  // either shows the intermission (if more remain) or navigates back.
  const advanceCue = () => {
    try {
      markWatched(realUrl, "movie");
    } catch { /* ignore */ }
    // Drop the just-finished movie from the cue.
    removeFromCue(realUrl);
    // Peek the next one WITHOUT popping it — the intermission's
    // "Skip" button will call playItem() which navigates fresh.
    const remaining = getCue();
    const next = remaining[0];
    if (next) {
      setCueIntermission(next);
    } else {
      // Cue drained — go back to Browse.
      navigate(-1);
    }
  };
  const onIntermissionFinish = () => {
    const next = cueIntermission;
    if (!next) {
      navigate(-1);
      return;
    }
    setCueIntermission(null);
    // Replace current watch entry so Back on the next movie takes the
    // user to Browse (not to the intermission of the previous title).
    navigate("/player/watch", {
      state: {
        stream_url: next.stream_url,
        name: next.name,
        logo: next.logo_url || "",
        type: "movie",
        cue_mode: true,
        poster: next.logo_url || next.poster || "",
      },
      replace: true,
    });
  };
  const onIntermissionSkip = onIntermissionFinish;

  // v1.4.62 — "Up Next" countdown state. Populated by a `timeupdate`
  // listener on the <video> element when a series episode is <=
  // COUNTDOWN_SECONDS from ending AND the parent shipped a non-empty
  // episode queue. The user can dismiss it ("Cancel") in which case
  // the countdown never re-arms for the rest of this playback.
  const [upNextRemaining, setUpNextRemaining] = useState(null); // seconds or null when hidden
  const [upNextDismissed, setUpNextDismissed] = useState(false);
  const nextEpisode = useMemo(() => {
    if (!Array.isArray(episodeQueue) || episodeQueue.length === 0) return null;
    if (contentType !== "series") return null;
    return episodeQueue[0];
  }, [episodeQueue, contentType]);

  // Use state for realUrl so we can hot-swap to a fallback (e.g. Xtream
  // catch-up has two URL formats — try the universal php endpoint first,
  // fall back to the /timeshift/.../.ts rewrite path on error).
  const [realUrl, setRealUrl] = useState(initialUrl);
  const [triedFallback, setTriedFallback] = useState(false);
  const onElectron = isElectron();
  const onAndroid = isAndroid();

  const [status, setStatus] = useState("loading"); // loading | playing | error | unsupported
  const [errorMsg, setErrorMsg] = useState("");
  // v1.3.35 — connection-limit pre-flight gate. Initial state is
  // "checking"; the effect below either resolves it to "ok" (continue
  // to playback) or navigates the user to Settings with a banner.
  const [preflight, setPreflight] = useState("checking");
  const enginePref =
    typeof window !== "undefined"
      ? getSelectedEngine(contentType)
      : "exoplayer";
  // Android always plays through the native plugin — both ExoPlayer and VLC
  // are bundled, the plugin routes to whichever engine the user picked in
  // Settings. We only need the WebView fallback when the plugin is missing.
  const androidUsingNative = onAndroid && isNativePlayerAvailable();

  // Sync realUrl when the user navigates to a different stream (e.g. picks
  // another programme) — location state has changed but this component may
  // be re-used by the router instead of being unmounted.
  //
  // v1.4.75 — Also reset the Up-Next state on every URL change so the
  // countdown banner re-arms for the newly-loaded episode. Previously
  // `upNextDismissed` was latched to `true` inside `playNextEpisode`
  // to stop the current playback from re-triggering the auto-advance
  // during the tiny window before the new stream_url landed — but the
  // flag was never cleared, so subsequent episodes never got a
  // countdown. Clearing here also ensures the visible metadata
  // (title / poster / rating) hydrates from the fresh location.state
  // instead of showing the previous episode's info.
  useEffect(() => {
    setRealUrl(initialUrl);
    setTriedFallback(false);
    setStatus("loading");
    setErrorMsg("");
    setPreflight("checking");
    setUpNextRemaining(null);
    setUpNextDismissed(false);
  }, [initialUrl]);

  // v1.3.35 — Xtream max-connections pre-flight. Hits player_api.php for
  // user_info and compares `active_cons` vs `max_connections`. If the
  // subscription is already at its concurrent-device cap, bounce the user
  // to Settings with a `connectionLimitReached` location-state flag — the
  // Settings page renders a red banner up top explaining the situation.
  // Skips silently for M3U-only playlists and for any pre-flight failure
  // (we always fail open — never block playback because of a network blip).
  //
  // v1.3.44 — SKIP the pre-flight for catch-up / time-shift URLs. Real-
  // world Xtream panels transiently bump active_cons when they spin up a
  // /streaming/timeshift.php or /timeshift/USER/PASS/... archive request,
  // causing this guard to false-positive every time the user clicks a
  // past programme in EPG. The Account Info card on Settings, called
  // moments later, then shows the truth (e.g. 0 active · 3 max). User
  // reported screenshots showing exactly this contradiction.
  const isCatchupUrl = (u) => {
    if (typeof u !== "string" || !u) return false;
    return (
      /\/streaming\/timeshift\.php/i.test(u) ||
      /\/timeshift\//i.test(u) ||
      /\/timeshift_abs-/i.test(u) ||
      /\/link_archive(_nt)?\//i.test(u) ||
      /\/play\/link_archive/i.test(u)
    );
  };
  useEffect(() => {
    if (!realUrl) return undefined;
    if (isCatchupUrl(realUrl)) {
      setPreflight("ok");
      return undefined;
    }
    let cancelled = false;
    setPreflight("checking");
    checkConnectionLimit().then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setPreflight("ok");
        return;
      }
      // Limit hit — abandon playback and push the user to Settings.
      navigate("/player/settings", {
        replace: true,
        state: {
          connectionLimitReached: true,
          active: res.active,
          max: res.max,
          providerName: res.providerName || null,
        },
      });
    });
    return () => { cancelled = true; };
  }, [realUrl, navigate]);

  // If the primary catchup URL fails and we have a fallback, swap to it
  // exactly once. Resets when the user navigates to a new programme.
  useEffect(() => {
    if (status === "error" && fallbackUrl && !triedFallback && realUrl !== fallbackUrl) {
      setTriedFallback(true);
      setStatus("loading");
      setErrorMsg("");
      setRealUrl(fallbackUrl);
    }
  }, [status, fallbackUrl, triedFallback, realUrl]);

  // Record history
  useEffect(() => {
    if (realUrl && name) {
      addToHistory({
        stream_url: realUrl,
        name,
        logo_url: logoUrl,
        content_type: contentType,
        // v1.4.30 — series aggregation fields (null for movies / live).
        series_id: seriesId,
        series_name: seriesName,
        season: seasonNum,
        episode: episodeNum,
        poster_url: posterUrl,
      });
      // v1.4.61 — DO NOT mark as watched here. Opening the player must not
      // count as "watched" (previously a 3-second peek flipped the ✓ overlay
      // and buried the item under the "watched" filter). `saveHistoryPosition`
      // now marks the item watched only once <= FINISHED_REMAINING_SECONDS
      // (60 s) of playback remain — before that it stays in Continue Watching.
    }
  }, [realUrl, name, logoUrl, contentType, seriesId, seriesName, seasonNum, episodeNum, posterUrl]);

  // ===== Electron: hand off to bundled mpv =====
  useEffect(() => {
    if (!realUrl || !onElectron) return;
    if (preflight !== "ok") return; // wait for connection-limit pre-flight
    if (!window.electronAPI?.playWithMpv) {
      setStatus("unsupported");
      setErrorMsg("Native engine missing — reinstall the player.");
      return;
    }
    let cancelled = false;
    window.electronAPI
      .mpvAvailable()
      .then((r) => {
        if (cancelled) return;
        if (!r?.available) {
          setStatus("unsupported");
          setErrorMsg("Bundled native engine is missing.");
          return;
        }
        return window.electronAPI.playWithMpv(realUrl, { title: name });
      })
      .then((r) => {
        if (cancelled || !r) return;
        if (r.ok) setStatus("playing");
        else {
          setStatus("error");
          setErrorMsg(r.error || "Could not launch the player.");
        }
      })
      .catch((e) => !cancelled && setStatus("error") && setErrorMsg(e?.message || "Error"));
    return () => {
      cancelled = true;
    };
  }, [realUrl, name, onElectron, preflight]);

  // ===== Android: native ExoPlayer (Media3) + WebView fallback =====
  useEffect(() => {
    if (!realUrl || onElectron) return;
    if (preflight !== "ok") return; // wait for connection-limit pre-flight
    // Built-in / only engine on Android → hand off to the bundled native
    // ExoPlayer (Media3) activity. WebView's <video> can't decode MPEG-TS /
    // AC3 which is what nearly every IPTV stream uses; ExoPlayer handles
    // all of them.
    if (onAndroid && isNativePlayerAvailable()) {
      let cancelled = false;
      setStatus("loading");
      setErrorMsg("");
      // v1.3.82 — Continue Watching resume for the native ExoPlayer
      // path. Non-live content gets its saved position handed off so
      // the activity's `player.seekTo` lands the user exactly where
      // they left the movie/episode.
      const startMs =
        contentType !== "live_tv"
          ? Math.floor(getResumePosition(realUrl) * 1000)
          : 0;
      playWithNativeExoPlayer(realUrl, {
        title: name,
        contentType,
        playlist: episodeQueue,
        startPositionMs: startMs,
        // v1.3.98 — Metadata for the native ExoPlayer overlay so the
        // Java activity can render the same poster + title + rating +
        // genre + date panel that PlayerOverlay draws on web.
        posterUrl: posterUrl || undefined,
        rating: Number.isFinite(rating) ? Number(rating) : undefined,
        genre: genre || undefined,
        releaseDate: releaseDate || undefined,
        // Live TV overlay fields (channel group + EPG). Watch.jsx
        // receives these in location.state from LiveTvWatch.playLive.
        channelGroup: channelGroup || undefined,
        currentEpg: currentProg || undefined,
        nextEpg: nextProg || undefined,
      })
        .then((r) => {
          if (cancelled) return;
          if (r.ok) {
            // v1.0.9 — Movie Cue mode: keep Watch.jsx mounted so we
            // can catch the "activity resumed" signal (native player
            // finishes -> WebView foregrounded again) and advance the
            // cue. Non-cue playback keeps the legacy behaviour of
            // popping this screen immediately.
            if (cueMode && contentType === "movie") {
              // No navigate(-1) — we sit here as an invisible host
              // until the native activity returns focus.
              return;
            }
            // Native player runs as a separate Android activity — there's
            // nothing for this React screen to render once the hand-off
            // succeeds. Pop ourselves off the history stack so the user
            // lands back where they came from (Browse / Live TV / etc.)
            // the moment they tap "back" inside the native activity.
            navigate(-1);
          } else {
            setStatus("error");
            setErrorMsg(r.error || "Native player failed to launch");
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setStatus("error");
            setErrorMsg(e?.message || "Native player error");
          }
        });
      return () => {
        cancelled = true;
      };
    }
    const video = videoRef.current;
    if (!video) return;

    setStatus("loading");
    setErrorMsg("");
    let hls = null;
    let mpegtsPlayer = null;
    const isHls = realUrl.includes(".m3u8");
    // v1.4.10 — MPEG-TS / .ts VOD detection. Browsers cannot decode
    // raw MPEG-TS via <video>. mpegts.js unpacks the .ts container and
    // feeds H.264 + AAC into MSE, which Chromium does support. Covers
    // the ~30% of Xtream providers that serve movies + episodes as
    // `movie/USER/PASS/12345.ts` instead of `.mp4`.
    // Detection: URL path ends in `.ts` OR `.mts` OR `.m2ts` before
    // any query string, AND we're not on HLS. Ignore trickplay params.
    const urlPath = (() => {
      try { return new URL(realUrl, window.location.origin).pathname.toLowerCase(); }
      catch { return realUrl.split("?")[0].toLowerCase(); }
    })();
    const isMpegts = !isHls && /\.(ts|mts|m2ts)$/.test(urlPath);
    // v1.3.82 — Continue Watching resume: look up any previously saved
    // position for this stream and seek to it once the media is ready.
    // Live TV is exempt (there's no meaningful "position" on a live
    // stream). Applied only for VOD-like content types.
    const isResumable = contentType !== "live_tv";
    const resumeFrom = isResumable ? getResumePosition(realUrl) : 0;
    const seekToResume = () => {
      if (resumeFrom > 0 && Number.isFinite(video.duration) && video.duration > resumeFrom) {
        try { video.currentTime = resumeFrom; } catch { /* ignore */ }
      }
    };

    if (isHls && Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 30,
        maxBufferLength: 30,
      });
      hlsRef.current = hls;
      hls.loadSource(realUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        seekToResume();
        // v1.3.97 — auto-apply the user's preferred audio language (if any)
        // for this playlist. No-op when the stream has only one track.
        applyPreferredAudioLang(video, hls);
        video.play().catch(() => {});
        setStatus("playing");
      });
      // The MANIFEST_PARSED event fires before HLS.js populates the
      // audio-tracks list on some streams. Also react to
      // AUDIO_TRACKS_UPDATED so we still catch the preference match
      // when tracks appear late.
      hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => {
        applyPreferredAudioLang(video, hls);
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          setStatus("error");
          setErrorMsg(data.details || "Stream error");
        }
      });
    } else if (isMpegts && mpegts.isSupported()) {
      // v1.4.10 — MSE-backed MPEG-TS demuxer for VOD .ts streams.
      // Non-live: keep the default liveBufferLatencyChasing OFF so
      // the seek bar / resume-point actually work.
      try {
        mpegtsPlayer = mpegts.createPlayer(
          { type: "mpegts", isLive: false, url: realUrl },
          {
            enableWorker: true,
            enableStashBuffer: true,
            stashInitialSize: 384,
            lazyLoad: false,
          }
        );
        mpegtsPlayer.attachMediaElement(video);
        mpegtsPlayer.load();
        mpegtsPlayer.play().catch(() => {/* autoplay blocked */});
        const onCanPlay = () => {
          seekToResume();
          applyPreferredAudioLang(video, null);
          setStatus("playing");
        };
        video.addEventListener("canplay", onCanPlay, { once: true });
        mpegtsPlayer.on(mpegts.Events.ERROR, (_type, details, info) => {
          setStatus("error");
          setErrorMsg(
            info?.msg || details || "Couldn't decode this .ts stream."
          );
        });
      } catch (err) {
        setStatus("error");
        setErrorMsg(err?.message || "Couldn't start MPEG-TS demuxer.");
      }
    } else {
      video.src = realUrl;
      const onPlaying = () => setStatus("playing");
      const onError = () => {
        setStatus("error");
        setErrorMsg("Couldn't decode this stream.");
      };
      const onLoadedMeta = () => {
        seekToResume();
        // v1.3.97 — native HLS / MSE path: apply preferred audio lang
        // once the tracks list is populated by the browser.
        applyPreferredAudioLang(video, null);
      };
      video.addEventListener("playing", onPlaying);
      video.addEventListener("error", onError);
      video.addEventListener("loadedmetadata", onLoadedMeta);
      video.play().catch(() => {});
      return () => {
        // v1.3.82 — flush position on unmount so the resume point is
        // preserved even if the user backs out mid-scene.
        if (isResumable && video && Number.isFinite(video.currentTime)) {
          saveHistoryPosition(realUrl, video.currentTime, video.duration || 0);
        }
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("error", onError);
        video.removeEventListener("loadedmetadata", onLoadedMeta);
        video.src = "";
      };
    }
    return () => {
      // v1.3.82 — also flush on HLS / MPEG-TS unmount path.
      if (isResumable && video && Number.isFinite(video.currentTime)) {
        saveHistoryPosition(realUrl, video.currentTime, video.duration || 0);
      }
      if (hls) hls.destroy();
      hlsRef.current = null;
      if (mpegtsPlayer) {
        try {
          mpegtsPlayer.unload();
          mpegtsPlayer.detachMediaElement();
          mpegtsPlayer.destroy();
        } catch {/* ignore */}
      }
    };
  }, [realUrl, onElectron, onAndroid, name, preflight, contentType]);

  // v1.4.75 — Progress ticker. Saves the current position to watch
  // history every 15 s while the web/hls.js player is active so the
  // resume point survives a browser refresh or crash. Only runs when
  // the video is actually playing to avoid dirtying localStorage while
  // paused or buffering.
  useEffect(() => {
    if (status !== "playing") return undefined;
    if (contentType === "live_tv") return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    const id = setInterval(() => {
      if (video.paused || !Number.isFinite(video.currentTime)) return;
      saveHistoryPosition(realUrl, video.currentTime, video.duration || 0);
    }, 15000);
    return () => clearInterval(id);
  }, [status, contentType, realUrl]);

  // v1.4.75 — Explicit "video finished" handler. When the media element
  // fires `ended` we (a) push the final position so the auto-mark logic
  // in saveHistoryPosition catches it and (b) unconditionally call
  // markWatched as a belt-and-braces guarantee — otherwise a movie that
  // ends on a tick-boundary or a series episode WITHOUT a follow-up in
  // the queue (last episode of the season) could exit the last 60 s
  // window without ever being flagged, leaving the tile without the
  // "Watched" badge until the user manually marks it. Ignored for Live
  // TV (which doesn't have an "ended" concept anyway).
  useEffect(() => {
    if (contentType === "live_tv") return undefined;
    const video = videoRef.current;
    if (!video || !realUrl) return undefined;
    const onEnded = () => {
      if (Number.isFinite(video.currentTime) && Number.isFinite(video.duration)) {
        saveHistoryPosition(realUrl, video.currentTime, video.duration || 0);
      }
      markWatched(realUrl, contentType);
      // v1.0.9 — In cue mode, advance the cue right here on web too
      // (native path handled by the visibilitychange effect above).
      if (cueMode && contentType === "movie") {
        advanceCue();
      }
    };
    video.addEventListener("ended", onEnded);
    return () => video.removeEventListener("ended", onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, realUrl, status, cueMode]);

  // v1.4.62 — "Up Next" countdown wiring. Runs only for series episodes
  // that have a follow-up in the queue. We poll the video's own time via
  // `timeupdate` (fires ~4×/second in Chromium) so the banner's countdown
  // stays in sync with the actual media clock — pausing the video pauses
  // the countdown for free. When the user clicks Cancel we set
  // `upNextDismissed`, which the effect uses as a hard latch so the
  // banner never re-appears for this playback.
  useEffect(() => {
    if (!nextEpisode) return undefined;
    if (upNextDismissed) return undefined;
    if (androidUsingNative || onElectron) return undefined; // native players handle their own up-next
    const video = videoRef.current;
    if (!video) return undefined;
    const evaluate = () => {
      const dur = Number(video.duration);
      const pos = Number(video.currentTime);
      // Live streams often surface `Infinity` / `NaN` for duration —
      // treat those as "no known runtime" and skip the countdown.
      if (!Number.isFinite(dur) || dur <= 0 || !Number.isFinite(pos)) {
        setUpNextRemaining(null);
        return;
      }
      const left = dur - pos;
      if (left <= COUNTDOWN_SECONDS && left > 0) {
        setUpNextRemaining(left);
      } else {
        setUpNextRemaining(null);
      }
    };
    const onEnded = () => {
      // The user may have paused near the end so `timeupdate` never
      // reached the < 0 window. When `ended` fires we jump the queue
      // immediately unless the user already cancelled.
      if (!upNextDismissed) playNextEpisode();
    };
    video.addEventListener("timeupdate", evaluate);
    video.addEventListener("durationchange", evaluate);
    video.addEventListener("loadedmetadata", evaluate);
    video.addEventListener("ended", onEnded);
    // v1.4.69 — Also poll at 500 ms so the visible countdown
    // updates smoothly (`timeupdate` fires ~4×/s in Chromium but as
    // slow as 250 ms elsewhere, and some HLS.js code paths pause
    // it during ABR switches — leaving the banner text "stuck" on
    // the previous second).
    const pollId = setInterval(evaluate, 500);
    return () => {
      video.removeEventListener("timeupdate", evaluate);
      video.removeEventListener("durationchange", evaluate);
      video.removeEventListener("loadedmetadata", evaluate);
      video.removeEventListener("ended", onEnded);
      clearInterval(pollId);
    };
  }, [nextEpisode, upNextDismissed, androidUsingNative, onElectron]);

  // v1.4.62 — Navigate to the next episode. `episodeQueue` from Browse.jsx
  // is already the *remaining* episodes in the season, so we hand
  // `queue.slice(1)` forward so this same countdown fires again on the
  // one after that. We use `replace: true` so pressing Back doesn't drop
  // the user onto the just-finished episode.
  const playNextEpisode = () => {
    if (!nextEpisode) return;
    const rest = Array.isArray(episodeQueue) ? episodeQueue.slice(1) : undefined;
    // v1.4.77 — Belt-and-braces "mark as watched + close history"
    // for the OUTGOING episode. Every auto-advance code path funnels
    // through here (10-s countdown, `ended` handler, manual "Play next"
    // button, and remote-control OK on the banner), so guaranteeing the
    // mark here means we no longer rely on the raced `ended` event
    // firing before the `<video>` element gets its src swapped. Also
    // force-writes history with `pos = dur` so the tile immediately
    // drops out of Continue Watching.
    try {
      const video = videoRef.current;
      const dur = video && Number.isFinite(video.duration) ? video.duration : 0;
      if (realUrl) {
        saveHistoryPosition(realUrl, dur || 0, dur || 0);
        markWatched(realUrl, contentType);
      }
    } catch { /* localStorage full — user still sees the next episode */ }
    setUpNextRemaining(null);
    setUpNextDismissed(true); // stop the current playback from re-triggering
    navigate("/player/watch", {
      state: {
        stream_url: nextEpisode.stream_url,
        name: nextEpisode.name,
        logo: nextEpisode.logo_url || "",
        type: "series",
        episode_queue: rest && rest.length > 0 ? rest : undefined,
        // Forward series identity so Continue Watching keeps aggregating
        // under the same show card.
        series_id: seriesId,
        series_name: seriesName,
        season: nextEpisode.season ?? nextEpisode.season_num ?? null,
        episode: nextEpisode.episode ?? nextEpisode.episode_num ?? null,
        poster: nextEpisode.poster_url || nextEpisode.logo_url || posterUrl || "",
      },
      replace: true,
    });
  };

  const cancelUpNext = () => {
    setUpNextRemaining(null);
    setUpNextDismissed(true);
  };

  if (!realUrl) {
    return (
      <div className="min-h-screen bg-black text-slate-300 flex flex-col items-center justify-center gap-3">
        <AlertTriangle className="w-10 h-10 text-amber-400" />
        <p>No stream provided.</p>
        <button onClick={() => navigate(-1)} className="text-orange-400 underline">
          Go back
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid={PLAYER.watchScreen}
      className="min-h-screen bg-black text-white flex flex-col"
    >
      {/* v1.0.9 — Movie Cue intermission overlay. Renders above everything
          else when the previous movie in the cue has finished and we're
          waiting to launch the next one. */}
      {cueIntermission && (
        <IntermissionOverlay
          minutes={getIntermissionMinutes()}
          nextMovie={cueIntermission}
          onFinish={onIntermissionFinish}
          onSkip={onIntermissionSkip}
        />
      )}
      <header className="flex items-center gap-3 px-5 py-3 bg-black/50 backdrop-blur border-b border-white/5">
        <button
          data-testid={PLAYER.btnBack}
          onClick={() => navigate(-1)}
          className="p-2 rounded-md hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 truncate min-w-0">
          <div className="text-xs uppercase tracking-wider text-orange-400">Now playing</div>
          <div className="font-semibold truncate">{name}</div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center relative bg-black">
        {/* Electron: status banner */}
        {onElectron && status === "loading" && (
          <div className="flex flex-col items-center text-slate-300">
            <Loader2 className="w-10 h-10 text-orange-400 animate-spin mb-4" />
            <p className="text-sm">Launching native player…</p>
          </div>
        )}
        {onElectron && status === "playing" && (
          <div className="flex flex-col items-center text-slate-200 max-w-md text-center px-6">
            <Cpu className="w-14 h-14 text-emerald-400 mb-4" />
            <p className="text-xl font-semibold mb-1">Playing in native engine</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Windows Media Foundation + DirectShow + DXVA handle this stream&apos;s codec.
              The mpv window has its own controls — close it to return here.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => window.electronAPI.playWithMpv(realUrl, { title: name })}
                className="text-sm bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5"
              >
                <Cpu className="w-3.5 h-3.5" />
                Re-launch player
              </button>
              <button
                onClick={() => navigate(-1)}
                className="text-sm text-orange-400 underline"
              >
                Back to menu
              </button>
            </div>
          </div>
        )}

        {/* Android (and any other WebView): HTML5 video — hidden when the
            native ExoPlayer activity is handling playback. */}
        {!onElectron && !androidUsingNative && (
          <>
            {status === "loading" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 z-10 pointer-events-none">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <span className="text-sm">Loading stream…</span>
              </div>
            )}
            <video
              ref={videoRef}
              data-testid={PLAYER.videoElement}
              // v1.3.94 — Movies/Series use PlayerOverlay. v1.3.95 — Live
              // TV uses LiveTvOverlay. Only Live TV without EPG state
              // falls back to the browser's default controls HUD.
              controls={!isVod && !isLiveTv}
              autoPlay
              playsInline
              className="w-full h-full max-h-[calc(100vh-60px)] bg-black"
            />
            {isVod && status !== "error" && status !== "unsupported" && (
              <PlayerOverlay
                key={realUrl /* v1.4.77 — force full remount on episode switch */}
                videoRef={videoRef}
                title={name}
                poster={posterUrl}
                rating={rating}
                genre={genre}
                releaseDate={releaseDate}
                onOpenSubtitles={() => setPicker("subtitles")}
                onOpenAudioTracks={() => setPicker("audio")}
                onOpenSettings={() => setPicker("settings")}
              />
            )}
            {isLiveTv && status !== "error" && status !== "unsupported" && (
              <LiveTvOverlay
                videoRef={videoRef}
                channelName={name}
                channelGroup={channelGroup}
                channelLogo={logoUrl}
                currentProgramme={currentProg}
                nextProgramme={nextProg}
                canZapUp={canZapUp}
                canZapDown={canZapDown}
                onZapUp={() => zapTo(zapQueue?.[zapIdx - 1])}
                onZapDown={() => zapTo(zapQueue?.[zapIdx + 1])}
                onOpenSubtitles={() => setPicker("subtitles")}
                onOpenAudioTracks={() => setPicker("audio")}
                onOpenSettings={() => setPicker("settings")}
              />
            )}
            <PlayerTrackPicker
              kind={picker}
              open={!!picker}
              onClose={() => setPicker(null)}
              videoRef={videoRef}
              hlsRef={hlsRef}
            />
            {/* v1.4.62 — Up-Next banner for series episodes. Only shown
                on the web/HLS path; native ExoPlayer / VLC on Android and
                Electron mpv drive their own auto-advance UI. */}
            {upNextRemaining != null && nextEpisode && (
              <UpNextCountdown
                nextEpisode={nextEpisode}
                remainingSeconds={upNextRemaining}
                onPlayNext={playNextEpisode}
                onCancel={cancelUpNext}
              />
            )}
          </>
        )}

        {/* Android Built-in (native player): status banner */}
        {androidUsingNative && status === "loading" && (
          <div className="flex flex-col items-center text-slate-300">
            <Loader2 className="w-10 h-10 text-orange-400 animate-spin mb-4" />
            <p className="text-sm">
              Launching {enginePref === "vlc" ? "libVLC" : "native ExoPlayer"}…
            </p>
          </div>
        )}
        {/* Error overlay */}
        {(status === "error" || status === "unsupported") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 z-20 px-4 text-center bg-black/85">
            <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
            <p className="font-semibold">
              {status === "unsupported" ? "Playback unavailable" : "Couldn't play this stream"}
            </p>
            <p className="text-xs text-slate-500 mt-1 max-w-md break-all">{errorMsg}</p>
            <div className="flex gap-3 mt-4 flex-wrap justify-center">
              {androidUsingNative && (
                <button
                  onClick={() => playWithNativeExoPlayer(realUrl, { title: name, contentType })}
                  className="text-sm bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5"
                >
                  <Cpu className="w-3.5 h-3.5" />
                  Retry native
                </button>
              )}
              {onElectron && (
                <button
                  onClick={() => window.electronAPI.playWithMpv(realUrl, { title: name })}
                  className="text-sm bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5"
                >
                  <Cpu className="w-3.5 h-3.5" />
                  Retry native
                </button>
              )}
              <button onClick={() => navigate(-1)} className="text-sm text-orange-400 underline">
                Pick another
              </button>
            </div>
            {/* v1.4.77 — PWA/web build: offer the native app when a
                stream fails to decode in the browser (H.265, AC-3,
                MPEG-2, etc.). Auto-hides on Capacitor / Electron. */}
            <GetNativeAppBanner />
          </div>
        )}
      </div>
    </div>
  );
}